"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader, Mail } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function NewsletterSignup() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  return <section id="newsletter-signup" ref={ref} className="relative w-full py-24 bg-primary overflow-hidden" style={{
    backgroundImage: "url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1542426/img/world_map_shipping_routes_texture.png)",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundBlendMode: "overlay"
  }}>
      <div className="absolute inset-0 bg-primary" style={{
      opacity: 0.95
    }} />

      <div className="relative z-10 max-w-2xl mx-auto px-6">
        <div className={`text-center mb-10 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
          <h2 className="text-4xl font-display font-bold text-white mb-4">
            Stay Informed with Market Insights
          </h2>
          <p className="text-white/80 text-lg">
            Subscribe to receive the latest analysis, trends, and updates from
            the global wholesale trade industry.
          </p>
        </div>

        <FormProvider className={`glass-effect p-8 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`} formId="newsletter_signup_blog_market_insights" sectionName="newsletter-signup" defaultValues={{
        first_name: "",
        last_name: "",
        email: "",
        company: ""
      }} validationRules={{
        email: {
          required: true,
          pattern: "email"
        }
      }} submitText="Subscribe Now" successMessage="Thank you for subscribing! You'll receive our latest market insights soon." errorMessage="Unable to process your subscription. Please try again.">
          <NewsletterForm />
        </FormProvider>
      </div>
    </section>;
}
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="text-center py-8">
        <div className="inline-flex items-center justify-center size-16 rounded-full bg-accent/20 mb-4">
          <Mail className="size-8 text-accent" />
        </div>
        <p className="text-foreground text-lg font-medium mb-6">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="border-primary/20 hover:bg-primary/5">
          Subscribe Another Email
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <FormField control={control} name="first_name" render={({
        field
      }) => <FormItem>
              <FormLabel>First Name</FormLabel>
              <FormControl>
                <Input placeholder="John" className="bg-white border-border focus-visible:border-accent transition-colors duration-[var(--duration-fast)]" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="last_name" render={({
        field
      }) => <FormItem>
              <FormLabel>Last Name</FormLabel>
              <FormControl>
                <Input placeholder="Doe" className="bg-white border-border focus-visible:border-accent transition-colors duration-[var(--duration-fast)]" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email address is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem className="mb-4">
            <FormLabel>Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@example.com" className="bg-white border-border focus-visible:border-accent transition-colors duration-[var(--duration-fast)]" {...field} />
            </FormControl>
            <FormDescription>
              We'll send the latest market insights to this address.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="company" render={({
      field
    }) => <FormItem className="mb-6">
            <FormLabel>Company (optional)</FormLabel>
            <FormControl>
              <Input placeholder="Acme Corp" className="bg-white border-border focus-visible:border-accent transition-colors duration-[var(--duration-fast)]" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 mb-6 bg-red-50 border border-red-200 rounded-sm">
          <p className="text-red-800 text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full btn-primary">
        {isSubmitting ? <>
            <Loader className="animate-spin size-4" />
            Subscribing...
          </> : <>
            <Mail className="size-4" />
            Subscribe Now
          </>}
      </Button>
    </Form>;
}